import type { UserDocListItem } from './interfaces'
import type { DocsView } from './doc'
import { backend } from '@/services/backend'
import { waitTimer } from '@/helpers/timer'
import { updateDocsUserDataCache, updateUserDocsCache } from '@/helpers/cache'

/**
 * Mark the document as favorite / unfavorite.
 */
export async function toggleDocFavorite(
  doc: UserDocListItem,
  docsView?: DocsView,
): Promise<void> {
  await setDocFavorite(doc, !doc.user.is_favorite, docsView)
}

/**
 * Set the document's favorite state to the requested value.
 * No request is made when the state already matches.
 */
export async function setDocFavorite(
  doc: UserDocListItem,
  shouldBeFavorite: boolean,
  docsView?: DocsView,
): Promise<void> {
  if (doc.user.is_favorite === shouldBeFavorite) {
    return
  }

  const result = shouldBeFavorite
    ? await backend.favorite(doc)
    : await backend.unfavorite(doc)
  doc.user.is_favorite = result.is_favorite

  // Reset cache to force fresh data loading.
  // Otherwise, we mark the book as favorite on the books page, for example,
  // then open the home page and the book is not marked as favorite (additional
  // page refresh is needed to see the favorite mark).
  await updateDocsUserDataCache(doc.id, result)
  await updateUserDocsCache(doc)

  // If a list view was provided, check if there is a "Favorites" view on
  // the same page. If so, update it.
  if (docsView) {
    await docsView.updateRelatedFavorites()
  }
}

export class DocFavoriteView {
  private doc: UserDocListItem
  private docsView?: DocsView

  public buttonDisabled: boolean = false
  public hint: 'none' | 'adding' | 'removing' = 'none'

  constructor(doc: UserDocListItem, docsView?: DocsView) {
    this.doc = doc
    this.docsView = docsView
  }

  async favoriteToggle($event: Event): Promise<void> {
    $event.preventDefault()
    $event.stopImmediatePropagation()

    this.hint = 'none'
    this.buttonDisabled = true

    if (this.doc.user.is_favorite) {
      this.hint = 'removing'
    } else {
      this.hint = 'adding'
    }
    setTimeout(() => (this.hint = 'none'), 2000)

    await Promise.all([
      toggleDocFavorite(this.doc, this.docsView),
      waitTimer(500),
    ])
    this.buttonDisabled = false
  }
}
